import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Envelope, Lock, GoogleLogo, ArrowRight } from '@phosphor-icons/react';
import { motion } from 'framer-motion';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F6FF] flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#5B21B6] to-[#7C3AED] p-12 flex-col justify-between">
        <div>
          <Link to="/" className="text-white text-3xl font-bold font-['Outfit']">
            MenoPath
          </Link>
          <p className="text-white/80 mt-2">Science-driven Actionable Insights</p>
        </div>
        
        <div className="space-y-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-6"
          >
            <p className="text-white text-lg leading-relaxed">
              "MenoPath helped me understand my body's changes and prepare for this new phase of life with confidence."
            </p>
            <p className="text-white/70 mt-4">— Sarah, 47</p>
          </motion.div>
          
          <div className="flex gap-4">
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 flex-1">
              <p className="text-white/60 text-sm">Beta Users</p>
              <p className="text-white text-2xl font-bold mt-1">8,000+</p>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 flex-1">
              <p className="text-white/60 text-sm">Biomarkers Tracked</p>
              <p className="text-white text-2xl font-bold mt-1">50M+</p>
            </div>
          </div>
        </div>
        
        <p className="text-white/50 text-sm">
          Trusted by thousands of women worldwide
        </p>
      </div>
      
      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden mb-8">
            <Link to="/" className="text-[#5B21B6] text-2xl font-bold font-['Outfit']">
              MenoPath
            </Link>
          </div>
          
          <h1 className="text-3xl font-bold text-[#1E1B4B] font-['Outfit']">Welcome back</h1>
          <p className="text-[#6B7280] mt-2">Sign in to continue to your dashboard</p>
          
          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#1E1B4B]">Email</Label>
              <div className="relative">
                <Envelope className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280] w-5 h-5" weight="duotone" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="pl-12 h-12 rounded-xl border-2 border-gray-200 focus:border-[#5B21B6] focus:ring-[#5B21B6]/20"
                  data-testid="login-email-input"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#1E1B4B]">Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280] w-5 h-5" weight="duotone" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pl-12 h-12 rounded-xl border-2 border-gray-200 focus:border-[#5B21B6] focus:ring-[#5B21B6]/20"
                  data-testid="login-password-input"
                  required
                />
              </div>
              <div className="text-right mt-2">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-[#5B21B6] hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#5B21B6] hover:bg-[#4C1D95] rounded-full text-base font-semibold"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Sign In
                  <ArrowRight className="ml-2 w-5 h-5" weight="bold" />
                </>
              )}
            </Button>
          </form>
          
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#F8F6FF] text-[#6B7280]">or continue with</span>
            </div>
          </div>
          
          <Button
            type="button"
            variant="outline"
            onClick={loginWithGoogle}
            className="w-full h-12 rounded-full border-2 border-gray-200 hover:border-[#5B21B6] hover:bg-[#EDE9FE] text-[#1E1B4B] font-medium"
            data-testid="google-login-btn"
          >
            <GoogleLogo className="mr-3 w-5 h-5" weight="bold" />
            Continue with Google
          </Button>
          
          <p className="text-center mt-8 text-[#6B7280]">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#5B21B6] font-semibold hover:underline" data-testid="register-link">
              Request early access
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
